import { useCallback, useContext, useEffect, useState } from "react";
import CategoryModal from "./CategoryModal/CategoryModal";
import {
  createCategoryAPI,
  getCategoriesAPI,
  updateCategoryAPI,
} from "../../../utils/api/support";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { normalizeCategories } from "../supportPage/helpers/supportHelpers";
import "./SupportCategory.css";
import { Button, Table, Repeat, ThumbsDown, ThumbsUp } from "../../ui";

export const CATEGORY_CODES = [
  "app_issue",
  "menu_update",
  "working_hours",
  "invoice_request",
  "rider_issue",

  "app_status",
  "meeting_request",
  "offer_request",
  "registration_request",

  "update_data",
  "other",
];

const TEXT = {
  ar: {
    title: "أقسام الدعم",
    subtitle: "إدارة أنواع الدعم وحقول النماذج",
    newCategory: "+ قسم جديد",
    loading: "جارٍ التحميل...",
    empty: "لا توجد أقسام",
    loadFailed: "تعذر تحميل الأقسام.",
    retry: "إعادة المحاولة",
    category: "القسم",
    code: "الرمز",
    fields: "الحقول",
    status: "الحالة",
    actions: "الإجراءات",
    active: "فعال",
    disabled: "معطل",
    edit: "تعديل",
    activate: "تفعيل",
    deactivate: "تعطيل",
    actionFailed: "تعذر حفظ التغيير. حاول مرة أخرى.",
  },
  en: {
    title: "Support Categories",
    subtitle: "Manage support types and dynamic form structure",
    newCategory: "+ New Category",
    loading: "Loading...",
    empty: "No categories found",
    loadFailed: "Categories could not be loaded.",
    retry: "Try again",
    category: "Category",
    code: "Code",
    fields: "Fields",
    status: "Status",
    actions: "Actions",
    active: "Active",
    disabled: "Disabled",
    edit: "Edit",
    activate: "Activate",
    deactivate: "Deactivate",
    actionFailed: "The change could not be saved. Please try again.",
  },
};

export default function SupportCategory() {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const t = TEXT[lang] || TEXT.en;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [actionError, setActionError] = useState("");

  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getCategoriesAPI(token);
      // Same shape handling as the ticket screen (array or { data }).
      setCategories(normalizeCategories(data));
      setLoadFailed(false);
    } catch (error) {
      console.error(error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setSelectedCategory(null);
    setOpen(true);
  };

  const openEdit = (c) => {
    setSelectedCategory(c);
    setOpen(true);
  };

  // Throws on failure so the modal stays open and shows why.
  const submit = async (payload) => {
    if (!can("support.category.manage")) return;
    const token = localStorage.getItem("auth-token");
    if (!token) return;

    if (selectedCategory) {
      await updateCategoryAPI(token, selectedCategory._id, payload);
    } else {
      await createCategoryAPI(token, payload);
    }

    setOpen(false);
    load();
  };

  const toggle = async (c) => {
    if (!can("support.category.manage")) return;
    const token = localStorage.getItem("auth-token");
    if (!token || togglingId) return;

    setTogglingId(c._id);
    setActionError("");
    try {
      await updateCategoryAPI(token, c._id, {
        active: !c.active,
      });
      await load();
    } catch (error) {
      console.error(error);
      setActionError(error?.message || t.actionFailed);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="support-page">
      <div className="support-header">
        <div>
          <h2 className="title">{t.title}</h2>
          <p className="subtitle">{t.subtitle}</p>
        </div>

        <Button variant="primary" appearance="filled" onClick={openCreate}>
          {t.newCategory}
        </Button>
      </div>

      {actionError && (
        <p className="support-action-error" role="alert">
          {actionError}
        </p>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="state" role="status">
            {t.loading}
          </div>
        ) : loadFailed ? (
          <div className="state" role="alert">
            {t.loadFailed}{" "}
            <Button size="sm" appearance="soft" onClick={load}>
              {t.retry}
            </Button>
          </div>
        ) : categories.length === 0 ? (
          <div className="state">{t.empty}</div>
        ) : (
          <Table
            className="support-category-table"
            data={categories}
            columns={[
              {
                key: "category",
                label: t.category,
                render: (c) => (
                  <div className="name">
                    {c.name?.[lang]}
                    <span className="desc">{c.description?.[lang] || "-"}</span>
                  </div>
                ),
              },
              {
                key: "code",
                label: t.code,
                render: (c) => <span className="code">{c.code}</span>,
              },
              {
                key: "fields",
                label: t.fields,
                render: (c) => (
                  <div className="fields-cell">
                    <div className="field-preview">
                      {c.formFields?.slice(0, 2).map((f) => (
                        <p key={f.key} className="field-chip">
                          {f.label?.[lang]}
                        </p>
                      ))}

                      {c.formFields?.length > 2 && (
                        <span className="field-more">+{c.formFields.length - 2}</span>
                      )}
                    </div>

                    <div className="field-hover">
                      {c.formFields?.map((f) => (
                        <div key={f.key} className="field-hover-row">
                          <span>{f.label?.[lang]}</span>
                          <span className="field-type">{f.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                key: "status",
                label: t.status,
                render: (c) => (
                  <span className={`badge ${c.active ? "active" : "inactive"}`}>
                    {c.active ? t.active : t.disabled}
                  </span>
                ),
              },
              {
                key: "actions",
                label: t.actions,
                render: (c) => {
                  const name = c.name?.[lang] || c.code;
                  return (
                    <div className="actions">
                      <Button
                        variant="primary"
                        appearance="soft"
                        size="sm"
                        rounded
                        icon={<Repeat size={16} />}
                        aria-label={`${t.edit}: ${name}`}
                        title={t.edit}
                        onClick={() => openEdit(c)}
                      />

                      <Button
                        variant={c.active ? "danger" : "success"}
                        appearance="soft"
                        size="sm"
                        rounded
                        icon={c.active ? <ThumbsDown size={16} /> : <ThumbsUp size={16} />}
                        aria-label={`${c.active ? t.deactivate : t.activate}: ${name}`}
                        title={c.active ? t.deactivate : t.activate}
                        disabled={Boolean(togglingId)}
                        loading={togglingId === c._id}
                        onClick={() => toggle(c)}
                      />
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </div>

      {open && (
        <CategoryModal
          category={selectedCategory}
          categoryCodes={CATEGORY_CODES}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}
